<style lang="scss">
  @use '../resources/scss/header_search.scss';
</style>

<script lang="ts">
  import { _ } from 'svelte-i18n'
  import { useNavigate } from 'svelte-navigator'
  import type Menu from '../api/rest/directus-cms/menus/model/Menu'
  import { commonWebsiteData, lang, menus } from '../stores/stores'
  import { searchNormalize } from '../utils'

  let select: HTMLDivElement | undefined
  let value = ''
  let focus = false
  let selecting = false
  let route: string | symbol = ''
  let navigate = useNavigate()
  const CONTACTS = Symbol('CONTACTS')

  // recursively adds translated routes according to locale
  function addRoute(routes: Map<string | symbol, string>, menu: Menu, lang: string) {
    if (!menu.is_route) return
    const translation = (menu.translations ?? []).find(
      translation => translation.languages_id?.lang === lang
    )
    if (!translation?.name) return
    const link = translation.link
    // ignore other locales, fallbacks and language switch
    if (!link.startsWith(`/${lang}/`)) return

    routes.set(link, translation.name)
  }

  function addRoutes(routes: Map<string | symbol, string>, menu: Menu, lang: string) {
    addRoute(routes, menu, lang)
    menu.submenus.forEach(submenu => addRoutes(routes, submenu, lang))
  }

  let routes: Map<string | symbol, string>
  let suggestions: Map<string | symbol, string>
  $: {
    routes = new Map<string | symbol, string>()
    // routes and subroutes
    $menus.forEach(menu => addRoutes(routes, menu, $lang))
    // contact
    const contacts = $commonWebsiteData?.footer_section?.contacts
    if (contacts) routes.set(CONTACTS, contacts)
  }
  $: suggestions = new Map(
    [...routes.entries()].filter(([, label]) =>
      searchNormalize(label).includes(searchNormalize(value))
    )
  )

  function tryNavigate(target: string | symbol) {
    if (typeof target === 'string') navigate(target)
    switch (target) {
      case CONTACTS:
        window.location.hash = '#contacts'
        break
    }
  }

  function handleSubmit() {
    if (route) tryNavigate(route)
    else {
      const defaultRoute = [...suggestions.keys()][0]
      if (defaultRoute) tryNavigate(defaultRoute)
    }
    value = ''
    route = ''
    selecting = false
  }
</script>

<svelte:window
  on:focusin={e => {
    const target = e.target
    if (!(target && target instanceof Node)) selecting = false
    else if (!(select && select.contains(target))) selecting = false
  }}
  on:click={e => {
    const target = e.currentTarget
    if (!(target && target instanceof Node)) selecting = false
    else if (!(select && select.contains(target))) selecting = false
  }}
/>

<form
  class="d-flex position-relative search-form flex-column"
  on:submit|preventDefault={handleSubmit}
>
  <input
    bind:value
    on:focus={() => (focus = true)}
    on:blur={() => {
      focus = false
      selecting = true
    }}
    type="text"
    name="q"
    placeholder={$_('search.placeholder')}
    class="search-field"
    autocomplete="off"
  />
  <div class="position-relative" bind:this={select}>
    {#if (focus || selecting) && value.length >= 2}
      <div
        class="bg-white border position-absolute left-0 w-100 overflow-auto"
        style="top: 100%; z-index: 1000; max-height:20rem"
      >
        {#each [...suggestions.entries()] as [target, label] (target)}
          <button
            type="button"
            class="suggestion d-block w-100 px-2 border-0 text-start"
            on:click={() => {
              value = label
              route = target
              handleSubmit()
            }}
          >
            {label}
          </button>
        {:else}
          <div class="empty px-2">{$_('search.empty-selection')}</div>
        {/each}
      </div>
    {/if}
  </div>
</form>
